<template>
  <section role="navigation" aria-label="Info panel" class="info-panel panel width-transition" :class="[
    !isOpen ? 'collapsed' : '',
    sessionBrowserPage ? 'session-browser-page' : '',
  ]">
    <div class="button-wrapper" :class="!isOpen ? 'collapsed' : ''">
      <!-- TODO: fix all this weird conditional structuring -->
      <IconButton
        v-if="!sessionBrowserPage && (!bookmarkList || !isOpen)"
        :icon="SettingsFilledWhite"
        alt="Settings"
        v-tooltip.right="'Settings'"
        @click="openSettings"
        :enterPress="openSettings"
        xsmall
        ariaLabel="Settings"
        ariaPopup="dialog"
      />
      <IconButton
        v-if="!isOpen && pageTitle"
        :icon="HomeIcon"
        alt="Home"
        v-tooltip.right="'Home'"
        @click="goToSessionBrowser"
        :enterPress="goToSessionBrowser"
        class="go-to-session-browser-button"
        xsmall
        ariaLabel="Home"
      />
      <IconButton
        :icon="ChevronLeftWhiteIcon"
        :alt="isOpen ? 'Close panel' : 'Open panel'"
        :rotated="!isOpen"
        class="collapse-button"
        @click="toggleInfoPanelOpen"
        :enterPress="toggleInfoPanelOpen"
        v-tooltip.right="'Toggle info panel open'"
        xsmall
        ariaLabel="Toggle info panel"
      />
    </div>

    <!-- TODO Contradicting classes -->
    <div class="expanded" :class="[!isOpen ? 'hide' : '', sessionBrowserPage ? 'show-actions' : '']">
      <div class="top-section">
        <header>
          <div aria-hidden="true" tabindex="-1">
            <LogoHeader></LogoHeader>
          </div>
          <h1>
            <IconButton
              v-if="pageTitle"
              :icon="HomeIcon"
              alt="Home"
              v-tooltip.right="'Home'"
              @click="goToSessionBrowser"
              :enterPress="goToSessionBrowser"
              class="go-to-session-browser-button"
              xsmall
              :aria-hidden="!pageTitle"
              ariaLabel="Home"
            />
          </h1>
          <h1 v-if="pageTitle">
            <div
              class="title-editable"
              @click="setShowTitleChangeModal(true)"
              @keydown="(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setShowTitleChangeModal(true);
                }
              }"
              :aria-label="`Edit session title`"
              :style="editTitleData && { cursor: 'pointer' }"
            >
              {{ pageTitle }}
              <img v-if="editTitleData" :src="EditIcon" alt="Edit" />
            </div>
          </h1>
        </header>

        <!-- Section below describes actions when browsing sessions -->
        <div class="actions-wrapper" v-if="sessionBrowserPage" :aria-hidden="!sessionBrowserPage">
          <div class="new-button-wrapper" v-if="isOpen">
            <RoundedButton
              class="new-menu-button"
              reverse
              text="NEW"
              biggerGap
              :image="PlusBlackIcon"
              fitWidth
              fitHeight
              adjustTextAlignment
              shadow
              @click="handleNewSessions"
              :enterPress="handleNewSessions"
              aria-haspopup="true"
              aria-controls="new_menu"
              aria-label="New"
            />
          </div>
          <div :class="{ collapsed: !isOpen }" class="nav-buttons-wrapper">
            <button 
              ref="collapsedNewButton"
              v-if="!isOpen" 
              v-tooltip.right="'New'" 
              @click="handleNewSessions"
              :onkeydown="handleKeyboardNewSessions"
              class="collapsed-new-button"
              aria-label="New"
              aria-haspopup="true"
            >
              <img :src="PlusBlackIcon" aria-hidden="true" aria-label="Create New Session"/>
              <span>New</span>
            </button>
            <button
              aria-label="Return to Home"
              :class="{
                active: this.$router.currentRoute.value.name === 'Home',
              }"
              v-tooltip.right="'Home'"
              :onclick="goToHome"
              :onkeydown="handleKeyboardGoToHome"
            >
              <img :src="HomeIcon" aria-hidden="true" />
              <span>Home</span>
            </button>
            <button
              aria-label="Archive"
              :class="{
                active: this.$router.currentRoute.value.name === 'Archive',
              }"
              v-tooltip.right="'Archive'"
              :onclick="goToArchive"
              :onkeydown="handleKeyboardGoToArchive"
            >
              <img :src="ArchiveIcon" aria-hidden="true" />
              <span>Archive</span>
            </button>
            <button
              aria-label="Settings"
              :class="{
                active: this.$router.currentRoute.value.name === 'Settings',
              }"
              :onclick="openSettings"
              :onkeydown="handleKeyboardOpenSettings"
              v-tooltip.right="'Settings'"
              aria-haspopup="dialog"
            >
              <img :src="SettingsFilledWhite" aria-hidden="true" />
              <span>Settings</span>
            </button>
          </div>
        </div>

        <Menu 
          class="table-menu-popup-panel" 
          ref="menu2" 
          id="actions_menu" 
          :model="menuProps" 
          :popup="true"
          :style="{ width: '22.4rem', height: '17.6rem' }"
          
        >
          <template #item="item">
            <div v-if="item.item.colors" class="color-buttons">
              <div v-for="color in item.item.colors" :key="color">
                <button 
                  class="color-button" 
                  :style="{backgroundColor: color,}" 
                  @click="changeBookmarkColor(color)" 
                  @keydown="($event) => {
                    if ($event.key === 'Enter') {
                      $event.preventDefault();
                      changeBookmarkColor(color);
                    }
                  }"
                  :aria-label="`Change bookmark color to ${color}`"  
                />
              </div>
            </div>

            <div v-if="!item.item.colors" class="table-menu-popup-panel-buttons">
              <button 
                :aria-label="item.item.label" 
                class="table-menu-popup-panel-button" 
                @click="item.item.command"
                @keydown="($event) => {
                  if ($event.key === 'Enter') {
                    $event.preventDefault();
                    item.item.command();
                  }
                }"  
              >
                {{ item.item.label }}
                <img :src="item.item.icon" alt="icon" />
              </button>
            </div>
          </template>
        </Menu>


        <Tabs v-if="isOpen && bookmarkList" value="0">
          <TabList>
            <Tab value="0">
              Details
            </Tab>
            <Tab value="1">
              Bookmark
            </Tab>
          </TabList>
          <TabPanels>

            <!-- Section below describes session details when viewing session -->
            <TabPanel value="0">
              <div class="info-blocks">
                <div v-for="item in data" :key="item.title" class="info-block" v-tooltip.bottom="item.tooltip">
                  <h3>{{ item.title }}</h3>
                  <template v-for="line in item.info" :key="line.text">
                    <p :class="line.type === 'secondary' ? 'secondary-info' : ''">
                      {{ line.text }}
                    </p>
                  </template>
                </div>
                <div class="info-block">
                  <div class="info-block-with-button">
                    <h3>
                      Supplementary materials
                      <span> ({{ additionalUploads.length }}/5)</span>
                    </h3>
                    <UploadButton
                      v-if="!isServiceProvider"
                      :type="'supplementaryFiles'"
                      :onInfoPanel="true"
                      :additionalUploads="additionalUploads" 
                      :sessionId="sessionData.id" 
                    />
                  </div>
                  <template v-for="upload in additionalUploads" :key="upload.id">
                    <div class="upload-info-block">
                      <div class="upload-file-info">
                        <p v-tooltip.bottom="upload.title" class="upload-file-name">
                          {{ formatTitle(upload.title) }}
                        </p>
                        <p class="secondary-file-info">
                          {{ formatBytes(upload.fileSize * 1024) }} &bull;
                          {{
                            upload.createdAt === "UPLOADING..."
                              ? upload.createdAt
                              : formatDate(upload.createdAt)
                          }}
                        </p>
                      </div>
                      <div class="action-buttons">
                        <IconButton 
                          :disabled="upload.status !== 'upload-complete'" 
                          xsmall 
                          class="download-button"
                          :icon="downloadGrey" 
                          alt="download-supplementary-material"
                          @click="downloadSupplementaryFile(upload)" 
                          :enterPress="() => downloadSupplementaryFile(upload)" 
                          ariaLabel="Download supplementary material" 
                        />
                        <IconButton 
                          :disabled="upload.status !== 'upload-complete'" 
                          v-if="!isServiceProvider" 
                          xsmall 
                          class="delete-button" 
                          :icon="trashGrey" 
                          alt="delete-supplementary-material"
                          @click="openConfirmDeleteDialog(upload)" 
                          :enterPress="() => openConfirmDeleteDialog(upload)" 
                          ariaLabel="Delete supplementary material" 
                        />
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </TabPanel>

            <!-- Section below describes bookmarks when viewing session -->
            <TabPanel value="1">
              <div class="bookmark-blocks add-scroll">
                <div v-for="bookmark in bookmarkList" :key="bookmark.id" class="bookmark-block">
                  <div class="bookmark-block-timestamp" :class="getTimestamp(bookmark.paragraphIndex) >= 3600
                    ? 'bookmark-block-timestamp-hours'
                    : 'bookmark-block-timestamp-mins'
                    ">
                    {{ bookmarkTimestampFormat(bookmark.paragraphIndex) }}
                  </div>
                  <div 
                    class="bookmark-block-text" 
                    :style="{
                    borderLeft: `0.2rem solid ${bookmark.color}`,
                    cursor: 'pointer',}" 
                    @click="jumpToTimestamp(bookmark.paragraphIndex)"
                    @keydown="($event) => {
                      if ($event.key === 'Enter') {
                        $event.preventDefault();
                        jumpToTimestamp(bookmark.paragraphIndex);
                      }
                    }"
                    aria-role="button"
                    :aria-label="`Jump to bookmark ${stripHtmlTags(bookmark.text)}`"
                  >
                    {{ stripHtmlTags(bookmark.text) }}
                  </div>

                  <div class="bookmark-block-bottom">
                      {{ formatDate(bookmark.updatedAt) }}
                    <IconButton 
                      :icon="ThreeDotsWhiteIcon" 
                      :alt="'Settings'" 
                      v-tooltip.top="'Settings'" 
                      xsmall
                      @click="(e) => toggle(e, bookmark)" 
                      :enterPress="(e) => toggle(e, bookmark)" 
                      ariaLabel="Settings" 
                    />
                    </div>
                  </div>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <!-- Section below describes bookmarks when viewing session -->
        <div v-if="!bookmarkList">
          <div class="info-blocks">
            <div v-for="item in data" :key="item.title" class="info-block">
              <h3>{{ item.title }}</h3>
              <template v-for="line in item.info" :key="line.text">
                <p :class="line.type === 'secondary' ? 'secondary-info' : ''">
                  {{ line.text }}
                </p>
              </template>
            </div>
          </div>
        </div>
      </div>


      <div class="bottom-section" v-if="!bookmarkList && isOpen">
        <div v-if="closeDownloadCard" class="download-card">
          <div class="download-card-text">
            <h2>Messenger Pigeon</h2>
            <h3>Free Download</h3>
          </div>
          <button
            class="download-icon-button"
            id="downloadMenuButton"
            @click="toggleDownloadMenu = !toggleDownloadMenu"
          >
            <img :src="DownloadWhiteIcon" />
          </button>
          <button
            class="close-download-card"
            @click="closeDownloadCard = false"
          >
            <img :src="CloseGrey" />
          </button>
          <div
            class="download-popup-wrapper"
            v-click-outside="toggleDownloadMenuClose"
            v-if="toggleDownloadMenu"
          >
            <div class="large-screen-menu platform-options-menu-wrapper">
              <div class="platform-options-menu menu">
                <div class="mobile-platform platform-option option">
                  Mobile
                  <img
                    :src="ChevronDownFilledWhite"
                    class="down-chevron rotate-cc90"
                  />
                  <div class="mobile-options-menu platform-option-menu menu">
                    <a
                      :href="LINKS.MP_DOWNLOAD_APPSTORE"
                      class="platform-option option"
                      target="_blank"
                    >
                      <img :src="AppleLogoWhite" class="platform-option-logo" />
                      App Store
                    </a>
                    <a
                      :href="LINKS.MP_DOWNLOAD_PLAYSTORE"
                      class="platform-option option"
                      target="_blank"
                    >
                      <img
                        :src="GooglePlayLogoWhite"
                        class="platform-option-logo"
                      />Google Play
                    </a>
                  </div>
                </div>
                <div class="desktop-platform platform-option option">
                  Desktop
                  <img
                    :src="ChevronDownFilledWhite"
                    class="down-chevron rotate-cc90"
                  />
                  <div class="desktop-options-menu platform-option-menu menu">
                    <a
                      :href="macOSLink"
                      class="platform-option option"
                      download
                    >
                      <img :src="AppleLogoWhite" class="platform-option-logo" />
                      Mac
                    </a>
                    <a
                      :href="windowsLink"
                      class="platform-option option"
                      download
                    >
                      <img
                        :src="WindowsLogoWhite"
                        class="platform-option-logo"
                      />
                      Windows
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div class="small-screen-menu platform-options-menu-wrapper">
              <div class="platform-options-menu menu">
                <div class="mobile-platform platform-option option">
                  Mobile
                  <img
                    :src="ChevronDownFilledWhite"
                    class="down-chevron rotate-cc90"
                  />
                  <div class="mobile-options-menu platform-option-menu menu">
                    <a
                      :href="LINKS.MP_DOWNLOAD_APPSTORE"
                      class="platform-option option"
                    >
                      <img :src="AppleLogoWhite" class="platform-option-logo" />
                      App Store
                    </a>
                    <a
                      :href="LINKS.MP_DOWNLOAD_PLAYSTORE"
                      class="platform-option option"
                    >
                      <img
                        :src="GooglePlayLogoWhite"
                        class="platform-option-logo"
                      />Google Play
                    </a>
                  </div>
                </div>
                <div class="desktop-platform platform-option option">
                  Desktop
                  <img
                    :src="ChevronDownFilledWhite"
                    class="down-chevron rotate-cc90"
                  />
                  <div class="desktop-options-menu platform-option-menu menu">
                    <a
                      :href="macOSLink"
                      class="platform-option option"
                      download
                    >
                      <img :src="AppleLogoWhite" class="platform-option-logo" />
                      Mac
                    </a>
                    <a
                      :href="windowsLink"
                      class="platform-option option"
                      download
                    >
                      <img
                        :src="WindowsLogoWhite"
                        class="platform-option-logo"
                      />
                      Windows
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        

        <div
          :aria-label="`User information: ${getAriaLabelForUserInfo()}`"
          class="user-info-wrapper"
        >
          <div aria-hidden="true" tabindex="-1" class="name-wrapper">
              <img :src="ProfileWhiteIcon" />
            <div class="user-full-name">
              {{ this.$store.state.user.currentUser.name }}
            </div>
          </div>  
          <div aria-hidden="true" tabindex="-1" class="user-email">
            {{ this.$store.state.user.currentUser.email }}
          </div>
          <div aria-hidden="true" tabindex="-1" class="user-type">
            {{ this.$store.state.user.currentUser.type }}
          </div>
        </div>

        <div class="user-info-actions">
          <button 
            class="bottom-button" 
            @click="logout"
            aria-label="Sign out"
            @keydown="($event) => {
              if ($event.key === 'Enter') {
                $event.preventDefault();
                logout();
              }
            }" 
          >
            <img alt="Leave icon" :src="LeaveWhiteIcon" aria-role="presentation"/>Sign out
          </button>
        </div>

        <div v-if="exitButtonText" class="exit-button-wrapper">
          <span v-if="exitButtonHelperText" class="helper-text">
            {{ exitButtonHelperText }}
          </span>
          <RoundedButton
            :text="exitButtonText"
            :onClick="exitButtonFunction"
            :enterPress="exitButtonFunction"
            slim
            wide
            shadow
            class="exit-button"
            ariaLabel="Exit"
          />
        </div>
      </div>

      <!-- Section below describes logging out controls -->
      <div class="bottom-section" v-else>
        <IconButton 
          v-if="!sessionBrowserPage" 
          :icon="SettingsFilledWhite" 
          alt="Settings" 
          @click="openSettings"
          xsmall 
          :enterPress="openSettings" 
          ariaLabel="Settings" 
        />
      </div>
    </div>

    <i v-if="tooltipInfo" class="pi pi-question-circle question-icon" :class="!isOpen ? 'hide' : ''"
      v-tooltip.top="{ value: tooltipInfo, escape: true }" />
    <Settings :isOpen="settingsOpenState" :activeTabIndex="settingsActiveTabIndexState"
      :setActiveTabIndex="setSettingsActiveTabIndex" :closeSettings="closeSettings" accountOption subscriptionOption
      feedbackAndHelpOption :keywordsOption="keywordsOption" :sttOption="sttOption"
      :captionsLanguageOption="captionsLanguageOption" :keywords="keywords"
      :addKeywordToDictionary="addKeywordToDictionary" :deleteKeywordFromDictionary="deleteKeywordFromDictionary"
      :changeLanguage="changeLanguage" :parentLanguage="parentLanguage" :changeSttType="changeSttType"
      :selectedSttType="selectedSttType" :pagesToShow="settingsPagesToShow" />
    <TitleChangeModal v-if="editTitleData" :itemId="editTitleData?.sessionId" :itemTitle="editTitleData?.sessionTitle"
      :visible="showTitleChangeModal" :setVisible="setShowTitleChangeModal" @updateSessionList="emitTitleChange" />
    <PaywallModal :visible="paywallModalVisible" :setVisible="setPaywallModalVisible" modal />
    <ConfirmationModal class="confirmation-modal" modal header="Delete File?" buttonTextConfirm="Confirm"
      buttonTextCancel="Cancel" smallerTitle ref="confirmationModal">
      <p>
        Are you sure you want to remove this supplementary file? Once deleted,
        it will no longer be available for the note-taker.
      </p>
    </ConfirmationModal>
    <Toast group="uploadStatus" position="bottom-center" />
  </section>
</template>

<script>
import { appName, LINKS } from "../assets/constants.js";
import RoundedButton from "../components/RoundedButton.vue";
import LogoHeader from "../components/LogoHeader.vue";
import ChevronLeftWhiteIcon from "../assets/images/Chevron-left-white.svg";
import ChevronDownFilledWhite from "../assets/images/Chevron-down-filled-white.svg";
import DownloadWhiteIcon from "../assets/images/Download-white.svg";
import SettingsWhiteIcon from "../assets/images/Settings-white.svg";
import RoundUpdateWhiteIcon from "../assets/images/Round-update-white.svg";
import LeaveWhiteIcon from "../assets/images/Leave-white.svg";
import ProfileWhiteIcon from "../assets/images/Profile-white.svg";
import SessionBrowserWhiteIcon from "../assets/images/Session-browser-white.svg";
import BulletListWhiteIcon from "../assets/images/Bullet-list-white.svg";
import HomeIcon from "../assets/images/Home-white.svg";
import ArchiveIcon from "../assets/images/Archive-white.svg";
import SettingsFilledWhite from "../assets/images/Settings-filled-white.svg";
import PlusBlackIcon from "../assets/images/Plus-black.svg";
import PlusWhiteIcon from "../assets/images/Plus-white.svg";
import MicrophoneDark from "../assets/images/Microphone-Dark.svg";
import UploadDarkIcon from "../assets/images/Upload-Dark.svg";
import IconButton from "../components/IconButton.vue";
import CloseGrey from "../assets/images/Close-grey.svg";
import {
  convertSecondsToHMS,
  convertSecondsToMS,
  logout,
  formatBytes,
  getCurrentUserSubscriptionDetails,
} from "../assets/helpers_legacy.js";
import Settings from "./Settings/Settings.vue";
import EditIcon from "../assets/images/EditIcon.svg";
import TitleChangeModal from "./TitleChangeModal.vue";
import ThreeDotsWhiteIcon from "../assets/images/ThreeDotsWhiteIcon.svg";
import TrashIcon from "../assets/images/Trash-white.svg";
import CopyIcon from "../assets/images/Copy-white.svg";
import AddToNotesIcon from "../assets/images/AddToNotes-white.svg";
import AppleLogoWhite from "../assets/images/Apple-Logo-white.svg";
import GooglePlayLogoWhite from "../assets/images/Google-Play-Logo-white.svg";
import WindowsLogoWhite from "../assets/images/Windows-Logo-white.svg";
import PaywallModal from "./Onboarding/PaywallModal.vue";
import { makeMPServerRequest } from "../assets/helpers";
import downloadGrey from "../assets/images/Downloader-grey.svg";
import trashGrey from "../assets/images/Trash-grey.svg";
import ConfirmationModal from "./ConfirmationModal.vue";
import UploadButton from "./UploadButton.vue";

const clickOutside = {
  beforeMount: (el, binding) => {
    el.clickOutsideEvent = (event) => {
      if (
        event.target == document.getElementById("downloadMenuButton") ||
        event.target ==
          document.getElementById("downloadMenuButton").children[0]
      ) {
        return;
      }
      // here I check that click was outside the el and his children
      if (!(el == event.target || el.contains(event.target))) {
        // and if it did, call method provided in attribute value
        binding.value();
      }
    };
    document.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted: (el) => {
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};

export default {
  directives: {
    clickOutside,
  },
  props: {
    addKeywordToDictionary: Function,
    captionsLanguageOption: Boolean,
    changeLanguage: Function,
    changeSttType: Function,
    data: Object,
    deleteKeywordFromDictionary: Function,
    exitButtonFunction: Function,
    exitButtonHelperText: String,
    exitButtonText: String,
    goToSessionBrowser: Function,
    isOpen: Boolean,
    keywords: Array,
    keywordsOption: Boolean,
    openNewMenu: Function,
    pageTitle: String,
    parentLanguage: Object,
    selectedSttType: String,
    sessionBrowserPage: Boolean,
    notetakingBrowserPage: Boolean,
    captioningBrowserPage: Boolean,
    sessionData: Object,
    settingsActiveTabIndex: Number,
    settingsOpen: Boolean,
    settingsPagesToShow: Array,
    sttOption: Boolean,
    toggleInfoPanelOpen: Function,
    tooltipInfo: String,
    userFullName: String,
    editTitleData: {
      type: Object,
      default: null,
    },
    bookmarkList: {
      type: Array,
      default: null,
    },
    pasteToNote: {
      type: Function,
      default: () => {
        return null;
      },
    },
    openNewSessionModal: {
      type: Function,
      default: () => {},
    },
    isServiceProvider: Boolean
  },

  components: {
    RoundedButton,
    IconButton,
    Settings,
    TitleChangeModal,
    PaywallModal,
    ConfirmationModal,
    LogoHeader,
    UploadButton,
  },

  data() {
    return {
      appName,
      LINKS,
      ChevronLeftWhiteIcon,
      ChevronDownFilledWhite,
      DownloadWhiteIcon,
      SettingsWhiteIcon,
      RoundUpdateWhiteIcon,
      LeaveWhiteIcon,
      ProfileWhiteIcon,
      SessionBrowserWhiteIcon,
      BulletListWhiteIcon,
      SettingsFilledWhite,
      PlusBlackIcon,
      MicrophoneDark,
      UploadDarkIcon,
      PlusWhiteIcon,
      EditIcon,
      ThreeDotsWhiteIcon,
      CloseGrey,
      TrashIcon,
      CopyIcon,
      AddToNotesIcon,
      HomeIcon,
      ArchiveIcon,
      visible: false,
      convertSecondsToMS,
      toggleDownloadMenu: false,
      closeDownloadCard: true,
      settingsOpenState: this.settingsOpen || false,
      settingsActiveTabIndexState: 0,
      AppleLogoWhite,
      GooglePlayLogoWhite,
      WindowsLogoWhite,
      macOSLink: "",
      windowsLink: "",
      items: [
        {
          label: "Record",
          icon: MicrophoneDark,
          command: () => {
            this.$router.push({
              name: "Recorder",
            });
          },
        },
        {
          label: "Upload",
          icon: UploadDarkIcon,
          command: () => {
            this.$router.push({
              name: "Upload",
            });
          },
        },
      ],
      collapsed: false,
      showTitleChangeModal: false,
      menuProps: [
        {
          label: "Color",
          colors: ["#FFD362", "#FFA69A", "#79DCFF", "#02FADA", "#E993FF"],
          icon: ThreeDotsWhiteIcon,
        },
        {
          label: "Copy",
          command: () => {
            this.copyText();
          },
          icon: CopyIcon,
        },
        {
          label: "Add to Notes",
          command: () => {
            this.addToNotes();
          },
          icon: AddToNotesIcon,
        },
        {
          label: "Delete",
          command: () => {
            this.deleteBookmark();
          },
          icon: TrashIcon,
        },
      ],
      seletectedBookmark: null,
      paywallModalVisible: false,

      downloadGrey,
      trashGrey,
      mediaUploadData: {},
      confirmationModal: null,
      uploadRecords: [],
      maxLength: 24,

      canUploadAudio: true,
      checkNewSessionsIntervalId: null,
    };
  },
  computed: {
    additionalUploads: {
      get() {
        return this.sessionData.uploads.filter(
          (upload) =>
            upload.type === "mp-supplementary" && upload.deletedAt === null
        );
      },
      set(value) {
        this.$store.state.user.sessionDetails.uploads = value;
      },
    },
  },

  watch: {
    settingsOpen(newVal) {
      this.settingsOpenState = newVal;
    },

    settingsActiveTabIndex(newVal) {
      this.settingsActiveTabIndexState = newVal;
    },
  },

  async created() {
    this.updateMaxLength();
    window.addEventListener("resize", this.updateMaxLength);
    
    this.currentUserSubscriptionDetails =
      await getCurrentUserSubscriptionDetails();
    
    if (!this.currentUserSubscriptionDetails.isActive) {
      // check new session availability every 5 minutes

      this.checkNewSessionsIntervalId = setInterval(this.checkNewSessions, 300000);
      await this.checkNewSessions();
    }
    // defaults to allowing new sessions if the user has an active subscription
  },

  mounted() {
    if (this.$router.currentRoute.value.name === "NoteTaker") {
      // TODO: fix this the proper way
      // removing this causes the supplementary material to not populate
      // this will most likely be fix when we upgrade nodeJS and PrimeVue
      // versions
      setTimeout(() => this.toggleInfoPanelOpen(), 20);
      setTimeout(() => this.toggleInfoPanelOpen(), 10);
    }
    this.confirmationModal = this.$refs.confirmationModal;
    this.getLatestReleaseUrls();
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.updateMaxLength);
  },

  unmounted() {
    if (!this.checkNewSessionsIntervalId) {
      clearInterval(this.checkNewSessionsIntervalId);
    }
  },
  

  methods: {
    stripHtmlTags(html) {
      if (!html) return '';
      // Create a temporary DOM element to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      // Return only the text content, which strips all HTML tags
      return tempDiv.textContent || tempDiv.innerText || '';
    },

    toggleDownloadMenuClose() {
      if (this.toggleDownloadMenu) {
        this.toggleDownloadMenu = !this.toggleDownloadMenu;
      }
    },

    getAriaLabelForUserInfo() {
      return `${this.$store.state.user.currentUser.name}, ` +
        `${this.$store.state.user.currentUser.email}, ` +
        `${this.$store.state.user.currentUser.type} user`;
    },

    logout() {
      logout(this.$router);
    },

    goToHome() {
      this.$router.push("/home");
    },

    goToArchive() {
      this.$router.push("/archive");
    },

    openSettings() {
      this.settingsOpenState = true;
      this.$emit("updateSettingsVisible", true);
    },

    handleKeyboardOpenSettings(event) {
      console.log(event.key)
      if (event.key === 'Enter') {
        event.target.blur();
        // setTimeout(() => {
          this.openSettings();
        // }, 500);
      }
    },

    handleKeyboardGoToArchive(event) {
      console.log(event.key)
      if (event.key === 'Enter') {
        event.target.blur();
        // setTimeout(() => {
          this.goToArchive();
        // }, 500);
      }
    },

    handleKeyboardGoToHome(event) {
      console.log(event.key)
      if (event.key === 'Enter') {
        event.target.blur();
        // setTimeout(() => {
          this.goToHome();
        // }, 500);
      }
    },

    handleKeyboardNewSessions(event) {
      console.log(event.key)
      if (event.key === 'Enter') {
        event.target.blur();
        // setTimeout(() => {
          this.handleNewSessions();
        // }, 500);
      }
    },

    closeSettings() {
      this.settingsOpenState = false;
      this.$emit("updateSettingsVisible", false);
    },

    setSettingsActiveTabIndex(index) {
      this.settingsActiveTabIndexState = index;
      this.$emit("updateSettingsActiveTabIndex", index);
    },

    setShowTitleChangeModal(show) {
      this.showTitleChangeModal = show;
    },

    emitTitleChange(newTitle) {
      this.$emit("updateTitle", newTitle);
    },

    formatDate(dateString) {
      const dateList = dateString.split("T")[0].split("-");
      return `${dateList[1]}.${dateList[2]}.${dateList[0]}`;
    },

    getTimestamp(paragraphIndex) {
      const transcriptData = this.$store.state.user.sessionDetails.transcriptData;
      const captionLine = transcriptData.sentences[paragraphIndex];
      return captionLine.startTimestamp / 1000;
    },

    bookmarkTimestampFormat(paragraphIndex) {
      const seconds = this.getTimestamp(paragraphIndex);
      return seconds >= 3600
        ? convertSecondsToHMS(this.getTimestamp(paragraphIndex))
        : convertSecondsToMS(this.getTimestamp(paragraphIndex));
    },

    toggle(event, elem) {
      this.selectedBookmark = elem;
      this.$refs.menu2.toggle(event);
    },

    copyText() {
      try {
        this.selectedBookmark.text &&
          navigator.clipboard.writeText(this.selectedBookmark.text);
      } catch (error) {
        console.error(error);
      }
    },

    addToNotes() {
      this.selectedBookmark.text &&
        this.pasteToNote(`<p>${this.selectedBookmark.text}</p>`);
    },

    async deleteBookmark() {
      let myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );
      myHeaders.append("Content-Type", "application/json");

      let requestOptions = {
        method: "DELETE",
        headers: myHeaders,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL +
          `self/uploads/${this.$store.state.user.sessionDetails.mediaUploadData.id}/` +
          `bookmarks/${this.selectedBookmark.id}`,
        requestOptions
      );
      const bookmarkData = await response.json();
      if (bookmarkData.status === "success") {
        this.$emit("removeBookmark", this.selectedBookmark);
      }
    },

    async changeBookmarkColor(color) {
      if (color === this.selectedBookmark.color) {
        return;
      }
      let myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );
      myHeaders.append("Content-Type", "application/json");

      let raw = JSON.stringify({
        color: color,
      });

      let requestOptions = {
        method: "PUT",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL +
          `self/uploads/${this.$store.state.user.sessionDetails.mediaUploadData.id}/` +
          `bookmarks/${this.selectedBookmark.id}`,
        requestOptions
      );
      const bookmarkData = await response.json();
      if (bookmarkData.status === "success") {
        this.selectedBookmark.color = color;
        this.$emit("changeBookmarkColor", this.selectedBookmark);
      }
    },

    jumpToTimestamp(paragraphIndex) {
      this.$emit("updateCurrentTime", this.getTimestamp(paragraphIndex));
      this.toggleInfoPanelOpen();
    },

    getLatestReleaseUrls() {
      let requestOptions = {
        method: "GET",
        redirect: "follow",
      };

      fetch(
        "https://api.github.com/repos/notetakingexpress/mp-desktop-releases/releases/latest",
        requestOptions
      )
        .then((response) => response.text())
        .then((result) => {
          let data = JSON.parse(result);
          const arr = data.assets;

          const macOSArr = arr.filter(
            (obj) =>
              obj.browser_download_url.includes(".dmg") &&
              !obj.name.includes("blockmap")
          );
          this.macOSLink = macOSArr[0].browser_download_url;

          const windowsArr = arr.filter(
            (obj) =>
              obj.browser_download_url.includes(".exe") &&
              !obj.name.includes("blockmap")
          );
          this.windowsLink = windowsArr[0].browser_download_url;
        })
        .catch((error) => console.log("error", error));
    },

    setPaywallModalVisible(visible) {
      this.paywallModalVisible = visible;
    },

    async handleNewSessions() {
      if (this.canUploadAudio) {
        this.openNewSessionModal(true);
      } else {
        this.paywallModalVisible = true;
      }
    },

    async openConfirmDeleteDialog(upload) {
      try {
        const confirmed = await this.$refs.confirmationModal.show();

        if (confirmed) {
          const myHeaders = new Headers();
          myHeaders.append(
            "Authorization",
            `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
          );
          const requestOptions = {
            method: "DELETE",
            headers: myHeaders,
            redirect: "follow",
          };
          fetch(
            process.env.VUE_APP_MP_SERVER_API_URL + "self/uploads/" + upload.id,
            requestOptions
          )
            .then((response) => response.text())
            .then((result) => result)
            .catch((error) => console.error(error));
          upload.deletedAt = new Date();
          this.$toast.add({
            severity: "contrast",
            detail:
              "Your supplementary file has been successfully deleted from the session.",
            group: "uploadStatus",
            life: 3000,
          });
        }
        // no action needed if the user cancels the dialog
      } catch (error) {
        console.error(`Error fetching details for item ${upload.id}:`, error);
      }
    },

    async downloadSupplementaryFile(upload) {
      const response = await makeMPServerRequest(
        "self/uploads/" + upload.id + "/get-presigned-url",
        "GET",
        "json",
        null
      );
    
      const link = document.createElement('a');
      link.href = response.data;
      link.download = upload.title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    updateMaxLength() {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1000) {
        this.maxLength = 21;
      } else {
        this.maxLength = 24;
      }
    },

    formatTitle(title) {
      if (title === null) {
        return (title = "No title"); // this should never happen
      } else if (title < 25) {
        return title;
      } else {

        const extensionMatch = title.match(/\.[^.]+$/);
        const extension = extensionMatch ? extensionMatch[0] : "";
        const nameLength = this.maxLength - extension.length - 3;

        const shortenedTitle =
          title.length > this.maxLength
            ? title.slice(0, nameLength) + ".." + extension
            : title;
        return shortenedTitle;
      }
    },
    formatBytes,
    async refreshUploadsList(sessionId) {
      const myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL + "self/sessions/" + sessionId,
        requestOptions
      );

      const data = await response.json();
      this.$store.commit("user/setSessionDetails", {
        ...this.$store.state.user.sessionDetails,
        uploads: data.data.uploads,
      });

      return data.data;
    },

    async checkNewSessions() {
      try {
        const response = await makeMPServerRequest("self/can-upload-audio", "GET", "json", null);
        this.canUploadAudio = response.data;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style lang="scss">
.info-panel {
  height: 100%;
  background: map-get($component-colours, info-panel-background);
  position: relative;
  width: $side-panel-width;
  min-width: $side-panel-width;
  overflow: hidden;
  border-right: 0.1rem solid map-get($component-colours, border-light-faint);

  @media only screen and (max-width: 1000px) {
    min-width: $side-panel-width-mobile;
    width: $side-panel-width-mobile;
  }

  .p-tab {
    font-size: 1.2rem;
    letter-spacing: 0.015rem;
  }

  .button-wrapper {
    position: absolute;
    top: 0;
    right: 1.6rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.8rem;
    margin: 2.4rem 0;
    color: map-get($colours, translucent-white-40);

    &.collapsed {
      flex-direction: column-reverse;
      gap: 3.2rem;
      width: 100%;
      right: 0;
      margin: 2.4rem 0;
    }

    .collapse-button {
      filter: brightness(40%);

      &:hover {
        filter: brightness(80%) !important;
      }
    }
  }

  .expanded {
    // overflow: auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    width: $side-panel-width;
    padding: 1.6rem;
    // padding-right: 0.8rem;
    transition: opacity 0.25s ease-in;

    @media only screen and (max-width: 1000px) {
      width: $side-panel-width-mobile;
    }

    &.show-actions.hide {
      opacity: 1;
      pointer-events: auto;
      padding: unset;
      width: auto;

      header,
      .bottom-section {
        opacity: 0;
        pointer-events: none;
      }
    }

    // &.hide {
    // transition: opacity 0.5s ease-out;
    // }

    .top-section {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      margin: 0;
      height: 100%;
      // max-height: 65rem;

      header {

        h1 {
          margin: 0;
          margin-top: 3.2rem;
          font-size: 2.4rem;
          font-weight: bold;
          font-family: $font-default;
          display: flex;
          align-items: center;

          .go-to-session-browser-button {
            margin-right: 0.8rem;
          }
        }
      }

      .info-blocks {
        overflow: auto;
        padding-right: 0.8rem;
        flex: 1;

        .info-block {
          margin-top: 2.4rem;

          h3 {
            margin: 0;
            margin-bottom: 0.4rem;
            color: map-get($component-colours, font-colour-secondary);
            font-weight: bold;
          }

          p {
            margin: 0;
            font-size: 1.6rem;
            color: map-get($component-colours, font-colour-primary);
          }

          .secondary-info {
            font-family: $font-secondary;
            color: map-get($component-colours, font-colour-secondary);
            font-size: 1.6rem;
          }

          .info-block-with-button {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;

            .upload-button {
              img {
                width: 1.4rem;
                height: 1.4rem;
              }

              height: 2.4rem;
              min-width: 7.9rem;
              @include callout;
              color: map-get($colours, grey-1);
              background-color: transparent;
              border: solid 1px #ffffff66;
              border-radius: 0.6rem;
              gap: 0.4rem;
              padding: 0.4rem;
              margin-bottom: 0.4rem;

              &:disabled {
                color: map-get($colours, grey-4) !important;
                opacity: 60%;

                img {
                  opacity: 40%;
                }
              }
            }
          }

          .upload-info-block {
            border-bottom: $separator-purple;
            padding: 1.2rem 0 0.8rem;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            min-width: 24.8rem;

            .upload-file-info {
              .upload-file-name {
                @include section-sub-header;
                flex-direction: column;
                max-width: 20rem;
                overflow: hidden;
                color: map-get($component-colours, white);
                white-space: nowrap;

                @media (max-width: 1000px) {
                  max-width: 17rem;
                }
              }

              .secondary-file-info {
                @include overline;
                text-transform: uppercase;
                color: map-get($component-colours, font-colour-secondary);
              }
            }

            .action-buttons {
              min-width: 7rem;

              .download-button {
                background: transparent;
                margin-right: 1.6rem;
              }

              .delete-button {
                background: transparent;
              }

              &:focus-visible {
                border: solid 2px map-get($colours, accent-purple);
                outline: solid 1px rgba(255, 233, 254, 40);
                box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
              }
            }
          }
        }
      }

      .bookmark-blocks {
        padding-right: 0.8rem;
        max-height: 100%;
        overflow-y: scroll;
        padding-bottom: 12rem;
        flex: 1;

        .bookmark-block {
          margin-top: 2.4rem;
          padding-top: 1rem;
          border-bottom: $separator-light;
          display: flex;
          flex-direction: column;
          align-items: left;
          color: white;

          .bookmark-block-timestamp {
            @include button;
            border-radius: 1.4rem;
            background-color: map-get($colours, accent-purple);
            color: white;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 2.4rem;
          }

          .bookmark-block-timestamp-hours {
            width: 6rem;
          }

          .bookmark-block-timestamp-mins {
            width: 5.5rem;
          }

          .bookmark-block-text {
            color: white;
            min-height: 1.5rem;
            margin-top: 1rem;
            margin-bottom: 1rem;
            padding-left: 1rem;
            min-height: 1.5rem;
            max-height: 6rem;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            line-clamp: 3;
            -webkit-box-orient: vertical;
          }

          .bookmark-block-bottom {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            margin-bottom: 1rem;
          }
        }
      }

      .actions-wrapper {
        .new-button-wrapper {
          height: 5.6rem;
          width: 12rem;
          margin-bottom: 2.4rem;

          .new-menu-button {
            border-radius: 3.2rem;
            padding: 1.2rem 1.6rem;
            letter-spacing: 0.125rem;

            &:focus-visible {
              border: solid 2px map-get($colours, accent-purple);
              outline: solid 1px rgba(255, 233, 254, 40);
              box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
            }

            img {
              width: 1.6rem;
              height: 1.6rem;
            }
          }
        }
      }
    }

    .bottom-section {
      margin-top: 0.75rem;
      display: flex;
      flex-direction: column;

      .exit-button-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 13.5rem;
        margin-top: 1.4rem;

        .helper-text {
          font-family: $font-secondary;
          font-size: 1.2rem;
          color: map-get($component-colours, font-colour-secondary);
          width: 100%;
          text-align: center;
          margin-bottom: 1rem;
        }
      }
      .download-card {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        height: 7.2rem;
        border-radius: 0.8rem;
        white-space: normal;
        border: none;
        outline: none;
        position: relative;
        font-family: $font-secondary;
        padding: 1.1rem 2rem;
        background-color: map-get($colours, accent-purple-2);
        color: map-get($colours, white);
        width: 100%;
        cursor: default;

        &:hover {
          .close-download-card {
            display: block;
          }
        }

        .download-card-text {
          text-align: left;
          h2 {
            font-size: 20px;
            color: map-get($colours, accent-purple);
            margin: 0;
          }
          h3 {
            text-transform: uppercase;
            color: map-get($colours, grey);
            letter-spacing: 0.125rem;
            font-weight: bold;
            font-size: 18px;
            margin: 0;
          }
        }
        .download-icon-button {
          border-radius: 0.4rem;
          border: 1px solid map-get($colours, accent-purple-4);
          padding: 0.6rem 1rem 0.6rem 1rem;
          background: map-get($colours, accent-purple-3);
          margin: 0.5rem 1rem;
          cursor: pointer;
          right: 2rem;

          @media only screen and (min-width: 1000px) {
            margin-left: 4rem;
          }
        }
        .close-download-card {
          display: none;
          margin: 0.2rem;
          position: absolute;
          top: 0.2rem;
          right: 0.2rem;
          cursor: pointer;
          background-color: transparent;
          padding: 0;
          img {
            width: 1.6rem;
            height: 1.6rem;
          }
        }

        .download-popup-wrapper {
          .down-chevron {
            width: 2.4rem;

            &.rotate-cc90 {
              transform: rotate(-90deg);
            }
          }

          .option {
            padding: 0rem 1.6rem;
            height: 4rem;
            background: map-get($colours, accent-purple-3);
            color: map-get($colours, white);
            font-size: 1.6rem;
            text-transform: initial;
            font-family: $font-secondary;
            font-weight: initial;
          }

          .menu {
            // padding: 0.8rem 0;
            height: 10rem;
            background: map-get($colours, accent-purple-3);
            border-radius: 0.6rem;
            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2),
              0 1px 10px 0 rgba(0, 0, 0, 0.12), 0 4px 5px 0 rgba(0, 0, 0, 0.14);
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .platform-options-menu-wrapper {
            position: absolute;
            top: 0;
            display: none;
            border-bottom: 0.8rem solid transparent;
            transform: translateX(-210%) translateY(-102%);

            .platform-options-menu {
              .platform-option {
                background: map-get($colours, accent-purple-3);
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;

                &:hover {
                  background: map-get($colours, accent-purple-4);
                  cursor: pointer;
                }

                &.mobile-platform {
                  &:hover {
                    .mobile-options-menu {
                      display: flex;
                    }
                  }
                }
                &.desktop-platform {
                  &:hover {
                    .desktop-options-menu {
                      display: flex;
                    }
                  }
                }

                .platform-option-menu {
                  position: absolute;
                  top: 0;
                  right: 0;
                  transform: translateX(100%);
                  display: none;
                  flex-direction: column;
                  width: 16rem;

                  .platform-option {
                    background: map-get($colours, accent-purple-3);
                    color: map-get($colours, white);
                    font-size: 1.6rem;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: flex-start;
                    cursor: pointer;
                    text-decoration: none;

                    &:hover {
                      background: map-get($colours, accent-purple-4);
                    }

                    .platform-option-logo {
                      margin-right: 0.8rem;
                      margin-left: 0;
                    }
                  }
                }
              }
            }
          }

          @include rwd(700) {
            .large-screen-menu {
              display: flex;
            }
          }

          @include max-width(700) {
            .small-screen-menu {
              display: flex;
            }
          }
        }
      }

      .user-info-wrapper {
        display: flex;
        flex-direction: column;
        padding: 0.8rem;
        border-radius: 0.8rem;
        font-family: $font-secondary;
        position: relative;
        margin-top: 0.6rem;
        margin-bottom: 0.6rem;

        &:hover {
          background: rgba(135, 117, 148, 0.2);

          .user-info-actions {
            display: block;
          }
        }

        &:focus-within {
          border: solid 1px rgba(255, 233, 254, 1);
          outline: solid 1px rgba(255, 233, 254, 1);
          box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
        }

        .user-info-actions {
          width: 100%;
          border-radius: 0.8rem;
          border: 0.1rem solid map-get($colours, accent-purple-2);
          overflow: hidden;
          background: map-get($colours, accent-purple-4);
          // position: absolute;
          // top: 0;
          // left: 0;
          // transform: translateY(-100%);
          display: flex;
          margin-top: 0.4rem;
          align-content: left;

          button {
            width: 100%;
            background: inherit;
            color: map-get($colours, white);
            outline: none;
            padding: 1.6rem;

            @include section-sub-header;

            img {
              margin-right: 0.8rem;
            }

            // &.top-button {
            //   padding: 2.4rem 1.6rem 1.6rem;
            // }
          }

          hr {
            width: calc(100% - 1.6rem - 1.6rem);
            border: none;
            height: 0.1rem;
            background-color: map-get($colours, accent-purple-2);
            color: map-get($colours, accent-purple-2);
            margin: 0 auto;
          }
        }

        .name-wrapper {
          display: flex;
          flex-direction: row;
          align-items: center;

          img {
            margin-right: 0.8rem;
          }

          .user-full-name {
            overflow: hidden;
            max-width: 100%;
            text-overflow: ellipsis;
            @include section-sub-header;
          }
        }

        .user-email {
          @include caption;
          margin-left: 3.2rem;
          color: map-get($colours, grey-2);
          overflow: hidden;
          max-width: 100%;
          text-overflow: ellipsis;
        }

        .user-type {
          font-size: 1.2rem;
          margin-left: 3.2rem;
          margin-top: 0.8rem;
        }

        .sign-out-button {
          color: map-get($component-colours, button-font-colour-primary);
          align-self: flex-start;
          margin-left: 3.2rem;
        }
      }

      .bottom-button {
        flex-direction: row;
        justify-content: center;
        align-items: left;
        display: flex;
        background-color: map-get($colours, accent-purple-4);
        color: map-get($colours, white);
        padding: 1.2rem 1.6rem;
        width: 100%;
        outline: none;
        padding: 1.6rem;

        @include section-sub-header;

        img {
          margin-right: 0.8rem;
        }

        &:focus-within {
          border: solid 1px rgba(255, 233, 254, 1);
          outline: solid 1px rgba(255, 233, 254, 1);
          box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
        }
      }
    }
  }

  .question-icon {
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    font-size: 1.5rem;
    color: rgba(255, 255, 255, 0.5);
  }
}

.new-menu-popup {
  width: calc($side-panel-width - 3.2rem);
  padding: 0.8rem 0rem;

  &:focus-visible {
    border: solid 2px map-get($colours, accent-purple);
    outline: solid 1px rgba(255, 233, 254, 40);
    box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
  }

  button {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 0.8rem;
    padding: 1.6rem 2.4rem;

    .action-icon {
      width: 2.4rem;
    }

    span {
      @include h3;
    }
  }

  @media only screen and (max-width: 1000px) {
    width: calc($side-panel-width-mobile - 3.2rem);
  }
}

.nav-buttons-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;

  button {
    @include h3;
    background: unset;
    border-radius: 8px 50px 50px 8px;
    flex: 1 0 100%;
    padding: 1.6rem 2.4rem;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 1.25rem;
    transition: all 0.15s ease-in-out;
    color: map-get($colours, white);

    &:hover,
    &.active {
      color: map-get($colours, white);
      background: transparentize(map-get($colours, accent-purple), 0.2);
      filter: unset;
    }

    &.active:hover {
      background: transparentize(map-get($colours, accent-purple), 0.4);
    }

    &:hover:not(.active, .collapsed-new-button) {
      background: map-get($colours, accent-purple-2) 20%;
      filter: unset;
    }

    &:focus-visible {
      border: solid 2px map-get($colours, accent-purple);
      outline: solid 1px rgba(255, 233, 254, 40);
      box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
    }
  }

  &.collapsed {
    flex-direction: column;
    align-items: center;
    gap: 3.2rem;

    button {
      border-radius: 1rem;
      flex: 0 0 auto;
      padding: 0.8rem;
      
      &:focus-visible {
        border: solid 2px map-get($colours, accent-purple);
        outline: solid 1px rgba(255, 233, 254, 40);
        box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
      }

      img {
        width: 1.9rem;
        height: 1.9rem;
      }

      span {
        display: none;
      }
    }
  }


  .collapsed-new-button {
    background: map-get($colours, white);

    &.active,
    &:hover {
      background: darken(map-get($colours, white), 5%);
    }

    &:focus-visible {
      border: solid 2px map-get($colours, accent-purple);
      outline: solid 1px rgba(255, 233, 254, 40);
      box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
    }
  }
}

.title-editable {
  @include h3;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  &:hover {
    img {
      display: block;
    }
  }

  img {
    display: none;
  }
}

.add-scroll {
  height: 55rem;
  overflow-y: scroll;
}

.table-menu-popup-panel {
  width: 22.4rem;
  height: 17.6rem;

  &.p-menu-overlay {
    background-color: map-get($colours, accent-purple-2);
  }

  &:focus-visible {
    border: solid 2px map-get($colours, accent-purple);
    outline: solid 1px rgba(255, 233, 254, 40);
    box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
  }

  .color-buttons {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    margin-bottom: 0.8rem;
    margin-top: 0.8rem;

    .color-button {
      border-radius: 50%;
      width: 2.4rem;
      height: 2.4rem;
    }
  }

  .table-menu-popup-panel-button {
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    background-color: transparent;
    padding: 0.8rem 1.6rem;
    font-size: 1.6rem;
    color: white;
  }
}
</style>
